/**
 * src/components/spatial/SpatialObjectDepositCard.tsx
 *
 * Putting money into a building, from the map.
 *
 * Same chrome as the hover card and the chat card, for the same reason:
 * a visitor should not be able to tell which part of the codebase served
 * them. What is different here is that this one asks for a signature, so
 * it is deliberately explicit about three things a payment screen has to
 * be explicit about — which object, which asset, and how much.
 *
 * The amount is typed in whole units and converted to stroops by string
 * arithmetic before it leaves the browser. `Math.round(value * 1e7)` is
 * wrong for values a person will actually type, and a deposit is the
 * wrong place to discover that.
 *
 * Withdrawal is not here. Collecting from an object is the beneficiary's
 * business and belongs on the operator surface, not on a card any
 * passer-by can open.
 */

import React, { useCallback, useMemo, useState } from 'react';
import { MapEntityTooltip } from '../map/MapEntityTooltip';
import {
  checkDeposit,
  depositToObject,
  describeBalance,
  treasuryBlockerText,
  type TreasurySigner,
  type TreasuryStatus,
} from '../../services/spatial/spatialTreasuryClient';

export interface SpatialObjectDepositCardProps {
  objectId: string;
  objectName: string;
  accentColor?: string;
  anchor: { x: number; y: number };
  viewport?: { width: number; height: number } | null;
  /** What the pinned card already loaded. Null while it is still loading. */
  status: TreasuryStatus | null;
  loading?: boolean;
  /** The connected wallet, if there is one. */
  wallet?: string | null;
  /** Turns the unsigned envelope into a signed one. Absent = no wallet. */
  signer?: TreasurySigner | null;
  /** Re-read the balance once a deposit lands. */
  onDeposited?: () => void;
  onClose: () => void;
}

const CARD_WIDTH = 300;

const rowStyle: React.CSSProperties = {
  padding: '8px 12px',
  borderBottom: '1px solid rgba(255,255,255,0.06)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
};

const labelStyle: React.CSSProperties = {
  fontSize: 11,
  color: '#64748b',
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
};

const valueStyle: React.CSSProperties = {
  fontSize: 12,
  color: '#e2e8f0',
  textAlign: 'right',
};

const noteStyle: React.CSSProperties = {
  fontSize: 11,
  color: '#94a3b8',
  lineHeight: 1.4,
};

export const SpatialObjectDepositCard: React.FC<SpatialObjectDepositCardProps> = ({
  objectId,
  objectName,
  accentColor = '#6366f1',
  anchor,
  viewport = null,
  status,
  loading = false,
  wallet = null,
  signer = null,
  onDeposited,
  onClose,
}) => {
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [landed, setLanded] = useState<{ hash: string; amount: string } | null>(null);

  const check = useMemo(
    () => checkDeposit(amount, status, wallet),
    [amount, status, wallet],
  );

  const assetCode = status?.asset?.code || null;
  // Only complain about an amount once something has been typed: an
  // empty field is not a mistake, it is a field.
  const amountProblem = amount.trim() && !check.ok && check.reason === 'bad_amount'
    ? treasuryBlockerText('bad_amount')
    : null;
  const standingProblem = !check.ok && check.reason !== 'bad_amount'
    ? treasuryBlockerText(check.reason)
    : null;

  const canSubmit = check.ok && !!signer && !busy;

  const submit = useCallback(async () => {
    if (!check.ok || !signer || !wallet || busy) return;

    setBusy(true);
    setFailure(null);

    const result = await depositToObject({
      objectId,
      depositor: wallet,
      amountStroops: check.stroops,
      asset: status?.asset?.address ?? null,
      signer,
    });

    setBusy(false);

    if (!result.ok) {
      // The contract's own words when it has any, ours when it does not.
      setFailure(treasuryBlockerText(result.reason) || String(result.reason));
      return;
    }

    setLanded({ hash: result.hash, amount: result.amount });
    setAmount('');
    onDeposited?.();
  }, [check, signer, wallet, busy, objectId, status, onDeposited]);

  return (
    <MapEntityTooltip
      data-testid="spatial-object-deposit-card"
      anchor={anchor}
      viewport={viewport}
      surfaceId={`object-deposit-${objectId}`}
      surfaceKind="object_deposit"
      width={CARD_WIDTH}
      estimatedHeight={280}
      accentColor={accentColor}
      title={objectName}
      badge={(
        <button
          type="button"
          aria-label="Close deposit"
          onClick={onClose}
          style={{
            border: 'none', background: 'rgba(255,255,255,0.1)', color: '#94a3b8',
            borderRadius: 4, padding: '1px 6px', fontSize: 12, cursor: 'pointer',
          }}
        >
          ✕
        </button>
      )}
    >
      <div style={rowStyle}>
        <span style={labelStyle}>Holds</span>
        <span style={valueStyle}>
          {loading && !status ? 'Reading…' : describeBalance(status?.balance)}
        </span>
      </div>

      <div style={rowStyle}>
        <span style={labelStyle}>Payee</span>
        {/* One word. Whether a treasury has somewhere to pay out to is
            something a would-be depositor should know; who that is, is
            not a hover card's business. */}
        <span style={valueStyle}>{status?.beneficiary ? 'Named' : 'None yet'}</span>
      </div>

      {landed ? (
        <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ ...noteStyle, color: '#4ade80' }}>
            {landed.amount}{assetCode ? ` ${assetCode}` : ''} sent to {objectName}.
          </div>
          <div style={{ ...noteStyle, fontFamily: 'monospace', wordBreak: 'break-all' }}>
            {landed.hash}
          </div>
        </div>
      ) : (
        <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="text"
              inputMode="decimal"
              value={amount}
              disabled={busy}
              placeholder="0.0"
              aria-label={`Amount to deposit${assetCode ? ` in ${assetCode}` : ''}`}
              onChange={(e) => { setAmount(e.target.value); setFailure(null); }}
              onKeyDown={(e) => { if (e.key === 'Enter' && canSubmit) void submit(); }}
              style={{
                flex: 1, minWidth: 0, background: 'rgba(255,255,255,0.06)', color: '#e2e8f0',
                border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8,
                padding: '6px 8px', fontSize: 13, fontFamily: 'inherit',
              }}
            />
            <span style={{ ...labelStyle, textTransform: 'none' }}>{assetCode || 'asset'}</span>
          </label>

          {(amountProblem || standingProblem || failure) && (
            <div style={{ ...noteStyle, color: '#fca5a5' }}>
              {failure || amountProblem || standingProblem}
            </div>
          )}

          <button
            type="button"
            disabled={!canSubmit}
            onClick={() => void submit()}
            style={{
              border: 'none', borderRadius: 8, padding: '7px 12px',
              fontSize: 12, fontWeight: 700,
              background: canSubmit ? 'rgba(99,102,241,0.3)' : 'rgba(255,255,255,0.06)',
              color: canSubmit ? '#818cf8' : '#475569',
              cursor: canSubmit ? 'pointer' : 'not-allowed',
            }}
          >
            {busy ? 'Waiting for signature…' : 'Deposit'}
          </button>

          <div style={noteStyle}>
            Contributions are held by {objectName}’s own treasury and can only be
            collected by its named payee. Depositing gives you no claim on them.
          </div>
        </div>
      )}
    </MapEntityTooltip>
  );
};

export default SpatialObjectDepositCard;
